import { useState, type ReactNode, type RefObject } from 'react';
import styled from '@emotion/styled/macro';
import {
  SvgIcon,
  SvgNames,
  tokens,
  Typography,
} from '@sunrun/experience-ui-components';
// eslint-disable-next-line import/no-restricted-paths
import { ListViewMenuOptions } from './ListViewMenuOptions';
// eslint-disable-next-line import/no-restricted-paths
import { ListViewFloaterMenu } from './ListViewTable';
// eslint-disable-next-line import/no-restricted-paths
import { ReferralMobileMeta } from './ReferralMobileMeta';
import { Prospect } from 'types/prospect';

type ListViewRowMobileBaseProps = {
  prospect: Prospect;
  isEven: boolean;
  statusPill: string;
  createdDate: string;
  name: string;
  actionsAriaLabel?: string;
  variant?: 'pipeline' | 'referral';
};

type ListViewRowMobilePipelineProps = ListViewRowMobileBaseProps & {
  variant?: 'pipeline';
  subtitle: ReactNode;
  multiline?: boolean;
  filledBackground?: boolean;
};

type ListViewRowMobileReferralProps = ListViewRowMobileBaseProps & {
  variant: 'referral';
  referredBy?: string;
  isFloaterMenuOpen: boolean;
  onDotsClick: () => void;
  floaterRef: RefObject<HTMLDivElement>;
};

type ListViewRowMobileProps =
  | ListViewRowMobilePipelineProps
  | ListViewRowMobileReferralProps;

const ListViewRowMobileReferral = ({
  prospect,
  statusPill,
  createdDate,
  name,
  actionsAriaLabel,
  referredBy,
  isFloaterMenuOpen,
  onDotsClick,
  floaterRef,
}: ListViewRowMobileReferralProps) => {
  const ariaLabel = actionsAriaLabel ?? `Actions for ${name}`;

  return (
    <ReferralCard $menuOpen={isFloaterMenuOpen}>
      <TopRow>
        <StatusPill size={12} fontWeight="medium">
          {statusPill || '-'}
        </StatusPill>
        <DotsMenuAnchor>
          <DotsButton
            type="button"
            aria-label={ariaLabel}
            onClick={(event) => {
              event.stopPropagation();
              onDotsClick();
            }}>
            <SvgIcon
              name={SvgNames.VerticalDotsSvg}
              width={24}
              height={24}
              color={tokens.BRAND_HEROBLUE}
            />
          </DotsButton>
          {isFloaterMenuOpen && (
            <ListViewFloaterMenu
              ref={floaterRef}
              prospect={prospect}
              placement="below"
            />
          )}
        </DotsMenuAnchor>
      </TopRow>
      <ProspectName fontWeight="medium" size={18}>
        {name}
      </ProspectName>
      <ReferralMobileMeta referredBy={referredBy} createdDate={createdDate} />
    </ReferralCard>
  );
};

const ListViewRowMobilePipeline = ({
  prospect,
  isEven,
  statusPill,
  createdDate,
  name,
  actionsAriaLabel,
  subtitle,
  multiline = false,
  filledBackground = false,
}: ListViewRowMobilePipelineProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const ariaLabel = actionsAriaLabel ?? `Actions for ${name}`;

  return (
    <PipelineCard
      $isEven={isEven}
      $filledBackground={filledBackground}
      $multiline={multiline}>
      <TopRow>
        <StatusPill>{statusPill || '-'}</StatusPill>
        <CreatedDate size="small" color={tokens.OFFWHITE_20}>
          Created {createdDate}
        </CreatedDate>
      </TopRow>
      <MainRow>
        <Identity>
          <Typography>{name}</Typography>
          {subtitle}
        </Identity>
        <ChevronButton
          type="button"
          aria-label={ariaLabel}
          aria-expanded={isExpanded}
          onClick={() => setIsExpanded((open) => !open)}>
          <SvgIcon
            name={isExpanded ? SvgNames.ChevronUp : SvgNames.ChevronDown}
            width={24}
            height={24}
          />
        </ChevronButton>
      </MainRow>
      {isExpanded && (
        <ExpandedSubMenu>
          <ListViewMenuOptions
            Child={({ onClick, label, icon }) => (
              <MenuItemRow key={String(label)} onClick={onClick}>
                <SvgIcon name={icon} width={16} height={16} />
                <Typography size="small">{label}</Typography>
              </MenuItemRow>
            )}
            prospect={prospect}
          />
        </ExpandedSubMenu>
      )}
    </PipelineCard>
  );
};

const ListViewRowMobile = (rowProps: ListViewRowMobileProps) =>
  rowProps.variant === 'referral' ? (
    <ListViewRowMobileReferral {...rowProps} />
  ) : (
    <ListViewRowMobilePipeline {...rowProps} />
  );

const ReferralCard = styled.div<{ $menuOpen?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 16px;
  box-sizing: border-box;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  background: transparent;
  border-bottom: 1px solid ${tokens.OFFWHITE_40};
  position: relative;
  ${({ $menuOpen }) =>
    $menuOpen &&
    `
    z-index: 1;
  `}
`;

const PipelineCard = styled.div<{
  $isEven: boolean;
  $filledBackground?: boolean;
  $multiline?: boolean;
}>`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px;
  box-sizing: border-box;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  min-height: ${({ $multiline }) => ($multiline ? '120px' : 'auto')};
  border-bottom: 1px solid rgb(208, 204, 195, 20%);

  ${({ $isEven, $filledBackground }) =>
    ($filledBackground || $isEven) &&
    `
    background-color: rgba(31, 38, 71, 0.04);
  `}
`;

const TopRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  min-width: 0;
  width: 100%;
  position: relative;
`;

const MainRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  gap: 8px;
`;

const Identity = styled.div`
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const ProspectName = styled(Typography)`
  text-align: left;
  word-break: break-word;
`;

const DotsMenuAnchor = styled.div`
  position: relative;
  flex-shrink: 0;
  margin-left: auto;
`;

const DotsButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  display: flex;
  align-items: center;
`;

const ChevronButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  display: flex;
  align-items: center;
  flex-shrink: 0;
`;

const StatusPill = styled(Typography)`
  display: inline-flex;
  width: fit-content;
  max-width: calc(100% - 40px);
  flex: 0 1 auto;
  min-width: 0;
  padding: 2px 16px !important;
  border-radius: 16px;
  background-color: ${tokens.INFO_80};
  color: rgba(73, 81, 125, 1);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const CreatedDate = styled(Typography)`
  flex-shrink: 0;
  margin-left: auto;
  text-align: right;
`;

const ExpandedSubMenu = styled.div`
  border-top: 1px solid ${tokens.OFFWHITE_40};
  padding: 12px;
  display: grid;
  padding-bottom: 0;
`;

const MenuItemRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 0;
  cursor: pointer;
`;

export { ListViewRowMobile };
